import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Formik } from 'formik'
import { filter } from 'lodash'
import TextField from '../../../../components/TextField'
import Select from '../../../../components/Select'
import { submissionTypes, seasonTypes } from '../../../../constants'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import { isTitleLocked } from '../../../../utils/titleLock'
import { extractLinks } from '../../../../utils/extractLinks'

const InfoContainer = styled.div`
  width: 100%;
`

const InfoHeader = styled.div`
  font-family: Hind;
  font-size: 20px;
  color: #000000;
  letter-spacing: 0;
  line-height: 16px;
`

const InfoDetails = styled.div`
  font-family: hind;
  font-size: 14px;
  color: #55575A;
  margin-top: 20px;
`

const InfoFields = styled.div`
  width: 100%;
  margin-top: 30px;
`

const DescriptionLinks = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  font-family: Hind;
  font-size: 14px;
  word-break: break-all;

  a {
    color: #55575A;
    text-decoration: underline;
    line-height: 22px;
  }
`

const GeneralInfo = ({ submission, saveSubmission, isUserView, isAdmin, workshopData, appliedTitleVersion, rowSaveFailed }) => {
  /**
   * Save when the value changed, OR when this field's last write failed.
   *
   * Skipping on equality alone stranded a failed edit that the editor then
   * reverted: the values agreed again, so no save fired, and the recorded
   * failure blocked every Copy tools action for the rest of the session.
   * Retrying the write clears it honestly -- or keeps it, if it fails again.
   */
  const onFieldBlur = (e, stored) => {
    const failed = rowSaveFailed ? rowSaveFailed(e.target.name) : false

    if (e.target.value !== stored || failed) saveSubmission(e.target.name, e.target.value)
  }
  const [location, setLocation] = useState(submission.location || '')
  let types = filter(submissionTypes, function (t) { return t.name !== 'Living' })

  // ONE value drives both the form remount and the autocomplete reattach
  // below. They must not drift: the remount replaces the #location input, and
  // an effect that does not re-run leaves Google Places bound to a node that
  // is no longer in the document -- autocomplete silently stops working until
  // the page is reloaded, with nothing on screen to say why.
  //
  // A counter that moves ONLY when the title tool applies a title, never on an
  // ordinary save. Keyed on submission.title instead, the title's own PUT
  // remounted this form while the editor had moved on to type in another field
  // here, discarding it -- exactly what the STY-579 note on
  // AdditionalInfoDefault warns about.
  const formKey = appliedTitleVersion || 0

  useEffect(() => {
    const createAutocomplete = async () => {
      let placesAPI = await new window.google.maps.places.Autocomplete( document.getElementById('location'), { types: ['(cities)'] })
      await placesAPI.setFields(['formatted_address'])
      await placesAPI.addListener('place_changed', () => {
        let place = placesAPI.getPlace()
        place = place.formatted_address
        if (place) {
          setLocation(place)
          saveSubmission('location', place)
        }
      })
    }
    createAutocomplete()
  }, [saveSubmission, formKey])

  const showDescription = submission.id <= 358705 || submission.type === 'other' || submission.status === 'quick' || submission.status === 'accepted-quick' || (isAdmin && submission.description)
  // The clickable links are an editor tool (STY-761), so they're hidden from
  // vendors and from an editor previewing the vendor view. They repeat what's
  // in the description, so they also need it shown -- a hidden description
  // must not leak its URLs.
  const showDescriptionLinks = isAdmin && !isUserView && showDescription

  return (
    <ErrorBoundary>
      <InfoContainer>
        <InfoHeader>Basic Information</InfoHeader>
        <InfoDetails>Please fill in the following details for your submission. The submission will not be considered without this information.</InfoDetails>
        <InfoFields>
          {/* STY-624: keyed so an externally applied title reaches this form.
              Exactly the bug STY-579 fixed for AdditionalInfo, in a second
              place: Formik seeds itself once, so a title chosen in the Title
              tab left this field showing the old one -- and the next blur
              compared that stale value against the new prop and saved the old
              title back, undoing the choice.

              Keyed on the title rather than given enableReinitialize, for the
              reason recorded on AdditionalInfoDefault: reinitialising on every
              prop change resets fields the editor is still typing in when some
              other field's save response lands. And keyed on the title rather
              than a shared counter, so saving any OTHER field here does not
              remount the form at all. */}
          <Formik
            key={formKey}
            initialValues={{ 
              title: submission.title || '',
              type: submission.type || '',
              location: submission.location || '',
              season: submission.season || '',
              description: submission.description || '',
              workshopHost: workshopData.host || ''
            }}
          >
            {({ values, handleChange }) => (
              <form>
                <TextField
                  value={values.title}
                  fullWidth
                  height={45}
                  name='title'
                  label='Title'
                  onChange={handleChange}
                  onBlur={(e) => onFieldBlur(e, submission.title)}
                  style={{marginBottom: 20}}
                  disabled={isTitleLocked(submission.status)}
                />
                {!isUserView ? <Select
                  options={types}
                  onChange={handleChange}
                  onBlur={(e) => onFieldBlur(e, submission.type)}
                  label='Type'
                  name='type'
                  fullWidth
                  labelWidth={35}
                  value={values.type}
                  style={{marginBottom: 20}}
                /> : null}
                {!isUserView && values.workshopHost !== '' ? <TextField
                  value={values.workshopHost}
                  fullWidth
                  height={45}
                  name='workshopHost'
                  label='Workshop Host'
                  style={{marginBottom: 20}}
                  disabled
                /> : null}
                <TextField
                  value={location}
                  fullWidth
                  height={45}
                  placeholder=''
                  name='location'
                  id='location'
                  label='Location'
                  onChange={(e) => setLocation(e.target.value)}
                  onBlur={(e) => onFieldBlur(e, submission.location)}
                  style={{marginBottom: 20}}
                />
                <Select
                  options={seasonTypes}
                  onChange={handleChange}
                  onBlur={(e) => onFieldBlur(e, submission.season)}
                  label='Season'
                  name='season'
                  fullWidth
                  labelWidth={49}
                  value={values.season}
                  style={{marginBottom: 20}}
                />
                {showDescription && (
                  <TextField
                    fullWidth
                    value={values.description}
                    id='description'
                    name='description'
                    label={submission.id > 358705 && submission.type !== 'other' ? 'Quick Link' : 'Description'}
                    disabled={submission.id > 358705 && submission.status !== 'quick' && submission.type !== 'other'}
                    multiline
                    rows={7}
                    onChange={handleChange}
                    onBlur={(e) => onFieldBlur(e, submission.description)}
                  />
                )}
                {showDescriptionLinks && extractLinks(values.description).length > 0 && (
                  <DescriptionLinks>
                    {extractLinks(values.description).map((link) => (
                      <a key={link} href={link} target='_blank' rel='noopener noreferrer'>{link}</a>
                    ))}
                  </DescriptionLinks>
                )}
              </form>
            )}
          </Formik>
        </InfoFields>
      </InfoContainer>
    </ErrorBoundary>
  )
}

export default GeneralInfo
